import { useState, useEffect, useRef } from "react";

interface TypewriterTextProps {
  staticText: string;
  words: string[];
  typingSpeed?: number;
  deletingSpeed?: number;
  pauseDuration?: number;
}

const TypewriterText = ({
  staticText,
  words,
  typingSpeed = 150,
  deletingSpeed = 100,
  pauseDuration = 2000
}: TypewriterTextProps) => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  // SEO: Initialize with first word for SSR/Prerendering to improve LCP & content relevance
  const [currentText, setCurrentText] = useState(words[0] || "");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    // Skip animation during pre-rendering to avoid hydration mismatch
    if (window.__PRERENDER__ || navigator.userAgent.includes("AgileCoachPrerender")) return;

    // Optimization: Pause animation when not visible to save CPU/Battery
    if (!isVisible) return;

    const currentWord = words[currentWordIndex];

    if (isPaused) {
      const pauseTimeout = setTimeout(() => {
        setIsPaused(false);
        setIsDeleting(true);
      }, pauseDuration);
      return () => clearTimeout(pauseTimeout);
    }

    if (!isDeleting && currentText === currentWord) {
      setIsPaused(true);
      return;
    }

    if (isDeleting && currentText === "") {
      setIsDeleting(false);
      setCurrentWordIndex((prev) => (prev + 1) % words.length);
      return;
    }

    const timeout = setTimeout(
      () => {
        setCurrentText((prev) => {
          if (isDeleting) {
            return currentWord.substring(0, prev.length - 1);
          } else {
            return currentWord.substring(0, prev.length + 1);
          }
        });
      },
      isDeleting ? deletingSpeed : typingSpeed
    );

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, isPaused, currentWordIndex, words, typingSpeed, deletingSpeed, pauseDuration, isVisible]);

  return (
    <span ref={containerRef} className="inline-block">
      <span>{staticText}</span>
      <span className="relative inline-block">
        <span suppressHydrationWarning className="bg-gradient-primary bg-clip-text text-transparent">
          {currentText}
        </span>
        <span className="animate-pulse ml-1 inline-block w-0.5 h-[0.8em] bg-primary align-middle" />
      </span>
    </span>
  );
};

export default TypewriterText;
